import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "./EnrollmentsWithDueDate.css";
import { apis } from "../../services/Api.jsx";
import card_img from "../../assets/images/card_img.png";
import lp_card_img from "../../assets/images/lp_card_img.png";
import certi_card_img from "../../assets/images/certi_card_img.png";
import jobaid_card_img from "../../assets/images/jobaid_card_img.png";
import vc from "../../assets/images/vc-icon.svg";
import classroomIcon from "../../assets/images/classroom.svg";
import activityIcon from "../../assets/images/activity.svg";
import docIcon from "../../assets/images/doc.svg";
import pdfIcon from "../../assets/images/pdf.svg";
import AiccIcon from "../../assets/images/aicc.svg";
import CpIcon from "../../assets/images/cp.svg";
import video from "../../assets/images/video.svg";
import pptIcon from "../../assets/images/pptIcon.svg";
import audio from "../../assets/images/audio.svg";
import excel from "../../assets/images/excel.svg";
import msword from "../../assets/images/msword.svg";
import scormtincan from "../../assets/images/scormtincan.svg";
import quiz from "../../assets/images/quiz.svg";
import { useNavigate } from "react-router-dom";
import Placeholder from "react-bootstrap/Placeholder";

const STATUS_COLORS = { Overdue: "#ff3333", "On Track": "#FFD600", Upcoming: "#ffa037ff"};
const STATUS_ORDER = ["Overdue", "Upcoming", "On Track"];
const cache = new Map();

// Utility functions
const validDate = (v) => v && !Number.isNaN(new Date(v).getTime());
const endOfDayTs = (v) => validDate(v) ? (new Date(v).setHours(23, 59, 59, 999)) : NaN;

// Image mapping
const IMAGE_MAP = {
    "Virtual Classroom": vc, Classroom: classroomIcon, Activity: activityIcon, DOC: docIcon,
    PDF: pdfIcon, AICC: AiccIcon, CP: CpIcon, VIDEO: video, OTHER: AiccIcon, PPT: pptIcon,
    AUDIO: audio, XLS: excel, MS_WORD: msword, SCORM12: scormtincan, SCORM2004: scormtincan,
    TINCAN: scormtincan, QUIZ: quiz,
    learningProgram: lp_card_img, certification: certi_card_img, jobAid: jobaid_card_img, course: card_img
};

// Batch processing utility
const runBatches = async (items, worker, concurrency = 3) => {
    const q = [...items];
    const workers = Array.from({ length: Math.min(concurrency, q.length) }, async () => {
        while (q.length) {
            const item = q.shift();
            try { await worker(item); } catch (_) { /* intentionally ignored */ }
        }
    });
    await Promise.all(workers);
};

// Core functions
const getEffectiveDeadline = (enr) => {
    if (validDate(enr?.attributes?.completionDeadline)) return enr.attributes.completionDeadline;
    const instId = enr?.relationships?.loInstance?.data?.id || enr?.relationships?.instance?.data?.id;
    const inst = instId ? cache.get(instId) : null;
    if (validDate(inst?.attributes?.completionDeadline)) return inst.attributes.completionDeadline;
    const loId = enr?.relationships?.learningObject?.data?.id;
    const lo = loId ? cache.get(loId) : null;
    return validDate(lo?.attributes?.completionDeadline) ? lo.attributes.completionDeadline : null;
};

const classify = (enr, nowTs) => {
    if (enr?.attributes?.hasPassed) return "Completed";
    const due = getEffectiveDeadline(enr);
    if (!due) return null;
    const dueTs = endOfDayTs(due);
    if (Number.isNaN(dueTs)) return null;
    if (dueTs < nowTs) return "Overdue";
    return Math.ceil((dueTs - nowTs) / 86400000) <= 30 ? "Upcoming" : "On Track";
};

const seedCacheFromLO = (res) => {
    if (!res) return;
    const arr = [];
    if (Array.isArray(res.data)) arr.push(...res.data);
    if (res.data && !Array.isArray(res.data)) arr.push(res.data);
    if (Array.isArray(res.included)) arr.push(...res.included);
    arr.forEach(it => it?.id && cache.set(it.id, it));
};

const extractCursor = (link) => {
    if (!link) return null;
    const m = /page\[cursor\]=([^&]+)/.exec(link);
    return m ? decodeURIComponent(m[1]) : null;
};

const resolveImage = (enrollment, lo) => {
    const ct = enrollment?.attributes?.contentType;
    if (ct && IMAGE_MAP[ct]) return IMAGE_MAP[ct];
    const lt = lo?.attributes?.loType;
    if (lt && IMAGE_MAP[lt]) return IMAGE_MAP[lt];
    return lo?.attributes?.imageUrl || card_img;
};

const useCssDonutSize = (fallback = 200) => {
    const [size, setSize] = useState(fallback);
    useEffect(() => {
        const read = () => {
            const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--donut-size"));
            setSize(Number.isFinite(v) && v > 0 ? v : fallback);
        };
        read();
        const ro = new ResizeObserver(read);
        ro.observe(document.documentElement);
        return () => ro.disconnect();
    }, [fallback]);
    return size;
}; 

const DetailItem = memo(function DetailItem({ e, status, getLo }) {
    const loId = e?.relationships?.learningObject?.data?.id;
    const lo = getLo(loId);

    const getTitle = () => {
        const instId = e?.relationships?.loInstance?.data?.id || e?.relationships?.instance?.data?.id;
        const inst = instId ? cache.get(instId) : undefined;
        const loName = lo?.attributes?.localizedMetadata?.[0]?.name;
        const instName = inst?.attributes?.localizedMetadata?.[0]?.name;
        return loName || instName || (loId ? loId.split(":").slice(-1)[0] : "N/A");
    };

    const title = getTitle();
    const desc =
        lo?.attributes?.localizedMetadata?.[0]?.description ||
        lo?.attributes?.LocalizedMetadata?.[0]?.overview ||
        lo?.attributes?.localizedMetadata?.[0]?.overview ||
        "No description available";
    const progress = e?.attributes?.progressPercent || 0;
    const due = getEffectiveDeadline(e);

    const naviagte = useNavigate()
    const crumbs = [{ label: "Home", url: "/" }];
    const goToCourseDetails = (course_id) => {
        naviagte("/CourseDetails/" + course_id, { state: { crumbs } });
    };

    return (
        <li className="detail-list-item" data-status={status} onClick={() => goToCourseDetails(lo?.id)} onKeyDown={() => goToCourseDetails(lo?.id)}  aria-hidden="true">
            <div className="enrollment-img-placeholder">
                <img src={resolveImage(e, lo)} alt={title} className="view-block-img" />
            </div>
            <div className="enrollment-info">
                <div className="enrollment-title" title={title}>{title}</div>
                <div className="enrollment-description" title={desc}>
                    {desc.length > 50 ? `${desc.slice(0, 50)}...` : desc}
                </div>
                <div className="enrollment-meta">
                    <span className="due-date">
                        Due Date: {due ? new Date(due).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}
                    </span>
                    <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            </div>
        </li>
    );
});

const DetailList = memo(function DetailList({ status, grouped, loMap, onBack, navigate }) {
    const items = grouped[status] || [];
    const getLo = useCallback((id) => loMap[id] || cache.get(id), [loMap]);

    return (
        <div className="enrollment-detail-view">
            <div className="detailed-header">
                <div className="back-title">
                    <button className="back-btn" aria-label="Back" onClick={onBack}>←</button>
                    <span className="detail-title">{status} ({items.length})</span>
                </div>
                <button
                    type="button"
                    className="view-all-link"
                    onClick={() => navigate("/overdue-enrollments")}
                >
                    View All
                </button>
            </div>
            <ul className="detail-list">
                {items.map((e) => (
                    <DetailItem key={e.id} e={e} status={status} getLo={getLo} />
                ))}
            </ul>
        </div>
    );
});

const EnrollmentsWithDueDate = () => {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailStatus, setDetailStatus] = useState(null);
    const [cacheTick, setCacheTick] = useState(0);
    const didFetch = useRef(false);
    const navigate = useNavigate();
    const donutSize = useCssDonutSize(200);

    const hydrateLOsFor = useCallback(async (enrs) => {
        const loIds = Array.from(
            new Set(
                (enrs || [])
                    .map(e => e?.id)
                    .filter(Boolean)
            )
        );
        const missing = loIds.filter(id => !cache.has(id));
        if (!missing.length) return;

        // break into chunks of 10
        const chunkSize = 10;
        const chunks = [];
        for (let i = 0; i < missing.length; i += chunkSize) {
            chunks.push(missing.slice(i, i + chunkSize));
        }

        await runBatches(
            chunks,
            async (ids) => {
                try {
                    const res = await apis.getEnrollmentsByIds(ids); // expects API to support multiple ids
                    seedCacheFromLO(res);
                } catch (e) {
                    console.error("getLearningObject failed for ids", ids, e);
                }
            },
            4
        );
        setCacheTick(v => v + 1);
    }, []);

    useEffect(() => {
        if (didFetch.current) return;
        didFetch.current = true;
        let alive = true;

        (async () => {
            setLoading(true);
            try {
                let allData = [];
                let cursor = undefined; // start with no cursor

                while (cursor !== null) {
                    const res = await apis.getHierarchialEnrollments(cursor);
                    if (!res) break;

                    // push data if present
                    if (Array.isArray(res.data)) {
                        allData = allData.concat(res.data);
                    } else if (res.data) {
                        allData.push(res.data);
                    }

                    // check for next cursor
                    const nextCursor = extractCursor(res?.links?.next);
                    cursor = nextCursor || null;
                }

                // sort enrollments by completionDeadline (latest first)
                allData.sort((a, b) => {
                    const aDeadline = a?.attributes?.completionDeadline
                        ? new Date(a.attributes.completionDeadline).getTime()
                        : 0;
                    const bDeadline = b?.attributes?.completionDeadline
                        ? new Date(b.attributes.completionDeadline).getTime()
                        : 0;

                    return bDeadline - aDeadline; // descending
                });

                if (!alive) return;
                setEnrollments(allData);

                // hydrate LO for all enrollments
                await Promise.all([
                    hydrateLOsFor(allData),
                ]);

            } catch (e) {
                console.error("Enrollments load error", e);
            } finally {
                if (alive) {
                    setLoading(false);
                    setCacheTick(v => v + 1);
                }
            }
        })();

        return () => {
            alive = false;
        };
    }, [hydrateLOsFor]);

    useEffect(() => {
        if (!detailStatus) return;
        const items = enrollments.filter(e => classify(e, Date.now()) === detailStatus);
        hydrateLOsFor(items);
    }, [detailStatus, enrollments, hydrateLOsFor]);

    const nowTs = useMemo(() => Date.now(), []);
    const grouped = useMemo(() => {
        return enrollments.reduce((acc, e) => {
            const s = classify(e, nowTs);
            if (!s || s === "Completed") return acc;
            acc[s].push(e);
            return acc;
        }, { Overdue: [], Upcoming: [], "On Track": [] });
    }, [enrollments, nowTs, cacheTick]);

    const chartData = useMemo(() => STATUS_ORDER.map(n => ({ name: n, value: grouped[n].length })), [grouped]);
    const total = useMemo(() => chartData.reduce((a, c) => a + c.value, 0), [chartData]);
    const allZero = total === 0;

    const labelAngles = useMemo(() => {
        const sum = total || 1;
        let cum = 0;
        return chartData.map(s => {
            const frac = s.value / sum;
            const a = frac * 360;
            const mid = cum + a / 2;
            cum += a;
            return { ...s, midAngle: mid };
        });
    }, [chartData, total]);

    const renderLabels = useMemo(() => {
        if (allZero) return null;
        
        const rOuter = donutSize * 0.35;
        const cx = donutSize / 2;
        const cy = donutSize / 2;
        const RAD = Math.PI / 180;

        return labelAngles
            .filter(l => l.value > 0)
            .map(l => {
                const r = rOuter + 24;
                const x = cx + r * Math.cos(-l.midAngle * RAD);
                const y = cy + r * Math.sin(-l.midAngle * RAD);
                const align = x < cx ? "right" : "left";
                return (
                    <span
                        key={l.name}
                        className="status-label-dyn"
                        style={{ left: x, top: y, textAlign: align, transform: "translate(-50%,-50%)" }}
                        aria-label={`${l.name} ${l.value}`}
                    >
                        <span className="status-name">{l.name}</span>
                        <span className="status-count">{String(l.value).padStart(2, "0")}</span>
                    </span>
                );
            });
    }, [labelAngles, donutSize, allZero]);

    const loMap = useMemo(() => {
        const map = {};
        enrollments.forEach(e => {
            const loId = e?.relationships?.learningObject?.data?.id;
            if (loId && cache.has(loId)) {
                map[loId] = cache.get(loId);
            }
        });
        return map;
    }, [enrollments, cacheTick]);

    if (loading) return (
        <div style={{width: "100%", height: "250px", border: "1px solid #b2b2b2"}}>
            <Placeholder animation="glow">
                <Placeholder style={{width: "100%", height: 20}}/>
            </Placeholder>
            <Placeholder animation="glow" style={{display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%"}}>
                <Placeholder style={{width: 133, height: 133, borderRadius: "50%"}}/>
            </Placeholder>
        </div>
    );

    return (
        <div className="enrollments-due-date-list">
            {!detailStatus && <h2>My Scheduled Enrollments</h2>}
            
            {!detailStatus ? (
                <div className={`donut-chart-container dynamic ${allZero ? "empty" : ""}`}>
                    {allZero ? (
                        <div className="empty-state">
                            <div className="empty-ring">
                                <div className="total-label" style={{marginTop: 20}}>Total</div>
                                <div className="total-count">0</div>
                            </div>
                            <p>No scheduled enrollments</p>
                        </div>
                    ) : (
                        <div className="donut-wrapper" aria-label="Due date status chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius="55%"
                                        outerRadius="70%"
                                        isAnimationActive={false}
                                        onClick={(_, idx) => {
                                            if (chartData[idx].value > 0) setDetailStatus(chartData[idx].name);
                                        }}
                                    >
                                        {chartData.map(entry => (
                                            <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="chart-total" role="presentation">
                                <div className="total-label">Total</div>
                                <div className="total-count">{total}</div>
                            </div>
                            <div className="status-labels-overlay">{renderLabels}</div>
                        </div>
                    )}
                </div>
            ) : (
                <DetailList
                    status={detailStatus}
                    grouped={grouped}
                    loMap={loMap}
                    onBack={() => setDetailStatus(null)}
                    navigate={navigate}
                />
            )}
        </div>
    );
};

export default EnrollmentsWithDueDate;